import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import api from "api/axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const STATUSES = ["draft", "submitted", "approved", "rejected"];

function TaxClaims() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "submitted",
    tax_year: new Date().getFullYear(),
    pfno: "",
  });

  const fetchAll = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (filters.status) params.set("status", filters.status);
      if (filters.tax_year) params.set("tax_year", String(filters.tax_year));
      if (filters.pfno) params.set("pfno", filters.pfno);

      const response = await api.get(`/tax/claims?${params.toString()}`);
      const payload = response?.data;
      const list = payload?.data ?? payload ?? [];

      setRows(Array.isArray(list) ? list : list?.data ?? []);
    } catch (error) {
      console.error("Failed to load tax claims:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const approve = async (id) => {
    try {
      await api.post(`/tax/claims/${id}/approve`, { remark: "Approved" });
      await fetchAll();
    } catch (error) {
      console.error("Approve failed:", error);
      alert(error?.response?.data?.message || "Failed to approve claim.");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/tax/claims/${id}/reject`, { remark: "Rejected" });
      await fetchAll();
    } catch (error) {
      console.error("Reject failed:", error);
      alert(error?.response?.data?.message || "Failed to reject claim.");
    }
  };

  const table = useMemo(
    () => ({
      columns: [
        { Header: "PFNO", accessor: "pfno" },
        { Header: "Year", accessor: "tax_year" },
        { Header: "Type", accessor: "claim_type_code" },
        { Header: "Annual Amount", accessor: "annual_amount" },
        { Header: "Status", accessor: "status" },
        {
          Header: "Action",
          accessor: "id",
          Cell: (cell) => {
            const record = cell?.row?.original || {};
            const claimId = record.id;
            const claimStatus = record.status;

            return (
              <MDBox display="flex" gap={1}>
                <MDButton
                  size="small"
                  onClick={() => approve(claimId)}
                  disabled={claimStatus !== "submitted"}
                >
                  Approve
                </MDButton>

                <MDButton
                  size="small"
                  variant="outlined"
                  onClick={() => reject(claimId)}
                  disabled={claimStatus !== "submitted"}
                >
                  Reject
                </MDButton>
              </MDBox>
            );
          },
        },
      ],
      rows,
    }),
    [rows]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" alignItems="center" justifyContent="space-between">
                <MDBox>
                  <MDTypography variant="h5">Tax Claims</MDTypography>
                  <MDTypography variant="button" color="text">
                    Review staff tax claims (exemptions) used in PAYE computation.
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    size="small"
                    label="PFNO"
                    value={filters.pfno}
                    onChange={(event) =>
                      setFilters((previous) => ({
                        ...previous,
                        pfno: event.target.value,
                      }))
                    }
                  />

                  <TextField
                    size="small"
                    label="Tax Year"
                    type="number"
                    value={filters.tax_year}
                    onChange={(event) =>
                      setFilters((previous) => ({
                        ...previous,
                        tax_year: Number(event.target.value),
                      }))
                    }
                    sx={{ width: 140 }}
                  />

                  <TextField
                    size="small"
                    select
                    label="Status"
                    value={filters.status}
                    onChange={(event) =>
                      setFilters((previous) => ({
                        ...previous,
                        status: event.target.value,
                      }))
                    }
                    sx={{ width: 160 }}
                  >
                    {STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>

                  <MDButton
                    variant="outlined"
                    onClick={fetchAll}
                    startIcon={<Icon>search</Icon>}
                    disabled={loading}
                  >
                    Apply
                  </MDButton>
                </MDBox>
              </MDBox>

              <Divider />

              <MDBox p={3}>
                <DataTable
                  table={table}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  canSearch
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default TaxClaims;
